import React, { useState } from "react";
import { Link } from "react-router-dom";
import AnimatedCounter from "../components/AnimatedCounter";
import TypewriterEffect from "../components/TypewriterEffect";
import { useIntersectionObserver } from "../components/IntersectionObserver";

/**
 * HomePage: Main landing page with hero section, problem overview, features, and demo PDF
 * Uses multiple custom components for animations and interactive elements
 */
const HomePage: React.FC = () => {
  // IntersectionObserver hooks: trigger animations when sections enter viewport
  const { ref: heroRef, isIntersecting: heroVisible } = useIntersectionObserver();
  const { ref: statsRef, isIntersecting: statsVisible } = useIntersectionObserver();
  // State for PDF viewer: can be minimized/expanded
  const [isPdfMinimized, setIsPdfMinimized] = useState(false);

  const togglePdfView = () => {
    setIsPdfMinimized(!isPdfMinimized);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="gradient-hero section-padding relative overflow-hidden">
        <div className="container-max text-center relative z-10">
          {/* Hero content fades in when it enters viewport */}
          <div
            ref={heroRef}
            className={`transition-all duration-1000 ease-out ${
              heroVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
            }`}
          >
            <h1 className="heading-1 mb-6">
              <TypewriterEffect
                text="Inventory and Valuation to Prepare for Insurance Claims"
                speed={80}
                className="block mb-2"
              />
            </h1>
            <p className="body-large mb-8 max-w-3xl mx-auto">
              Professional inventory and valuation for homeowners in disaster-prone zones.
              Start with a 30-60 minute video call with our specialist, who records and documents your home inventory for comprehensive, professional results.
            </p>
            <div className="mb-8">
              <p className="text-xl text-primary-600 font-semibold mb-4">
                Free consultation for the first 25 users
              </p>
              <p className="text-lg text-gray-600 mb-6">
                We need your feedback!
              </p>
            </div>
            <Link to="/try" className="btn-primary inline-block">
              Get Started Now
            </Link>
          </div>
        </div>

        {/* Background Image */}
        <div className="absolute inset-0 z-0 opacity-10">
          <img
            src="/images/front.jpg"
            alt="Modern home front view"
            className="w-full h-full object-cover"
          />
        </div>
      </section>

      {/* Problem Overview with Animated Stats */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="heading-2 mb-6">The Problem</h2>
            <p className="body-large max-w-3xl mx-auto">
              Many home insurance claims never get filed. People in hurricane/wildfire zones like Florida & California lose everything. No existing easy way to document or value belongings.
            </p>
          </div>

          {/* Animated Statistics: Counters animate when this section becomes visible */}
          <div
            ref={statsRef}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16"
          >
            <div className="card p-8 text-center hover-lift">
              <div className="text-4xl font-bold text-primary-600 mb-4">
                <AnimatedCounter
                  end={50}
                  suffix="%+"
                  className={statsVisible ? 'animate-fade-in' : ''}
                />
              </div>
              <h3 className="heading-3 mb-4">Unfiled Claims</h3>
              <p className="body-medium">
                Many homeowners never file insurance claims due to lack of documentation
              </p>
            </div>

            <div className="card p-8 text-center hover-lift" style={{animationDelay: '0.1s'}}>
              <div className="text-4xl font-bold text-primary-600 mb-4">
                <AnimatedCounter
                  end={100}
                  suffix="B+"
                  prefix="$"
                  className={statsVisible ? 'animate-fade-in' : ''}
                />
              </div>
              <h3 className="heading-3 mb-4">Natural Disaster Damages</h3>
              <p className="body-medium">
                Annual damages to the world from natural disasters
              </p>
            </div>

            <div className="card p-8 text-center hover-lift" style={{animationDelay: '0.2s'}}>
              <div className="text-4xl font-bold text-primary-600 mb-4">
                <AnimatedCounter
                  end={0}
                  suffix="%"
                  className={statsVisible ? 'animate-fade-in' : ''}
                />
              </div>
              <h3 className="heading-3 mb-4">Easy Solutions</h3>
              <p className="body-medium">
                No existing easy way to document or value belongings
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card p-8 text-center hover-lift animate-fade-in">
              <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <h3 className="heading-3 mb-4">Scan</h3>
              <p className="body-medium">
                Upload photos of your belongings and our specialist manually identifies and catalogs them
              </p>
            </div>

            <div className="card p-8 text-center hover-lift animate-fade-in" style={{animationDelay: '0.1s'}}>
              <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="heading-3 mb-4">Value</h3>
              <p className="body-medium">
                Get accurate valuations for each item with professional analysis and specialist review
              </p>
            </div>

            <div className="card p-8 text-center hover-lift animate-fade-in" style={{animationDelay: '0.2s'}}>
              <div className="w-16 h-16 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="heading-3 mb-4">Protect</h3>
              <p className="body-medium">
                Generate insurance-ready documentation to ensure fair compensation when disaster strikes
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose ReadyToClaim - Combined Section */}
      <section className="section-padding gradient-primary">
        <div className="container-max">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="heading-2 mb-6">Why Choose ReadyToClaim</h2>
            <p className="body-large max-w-3xl mx-auto">
                      From furniture to electronics, jewelry to artwork - our specialist conducts a comprehensive video call to document your belongings, then we provide precise valuations and professional documentation
                    </p>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-16">
            <div className="animate-fade-in">
              <h3 className="heading-3 mb-8">Comprehensive Documentation</h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Specialist Video Call</h4>
                    <p className="text-gray-600">30-60 minute recorded Zoom session with our expert to comprehensively document your home inventory</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Comprehensive Documentation</h4>
                    <p className="text-gray-600">Our specialist creates detailed inventory from the recorded session, ensuring nothing is missed</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Professional Analysis</h4>
                    <p className="text-gray-600">We analyze the documentation and provide accurate market valuations</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Insurance Ready</h4>
                    <p className="text-gray-600">Professional documentation format accepted by all major insurers</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="animate-fade-in" style={{animationDelay: '0.2s'}}>
              <div className="relative group">
                <div className="w-full h-64 bg-gradient-to-br from-primary-100 to-blue-100 rounded-3xl shadow-2xl hover:shadow-3xl transition-all duration-500 group-hover:scale-105 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-primary-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-700 mb-2">Professional Documentation</h3>
                    <p className="text-sm text-gray-600">Professional inventory service with specialist review</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Document Everything Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="animate-fade-in group">
              <div className="relative overflow-hidden rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 group-hover:-translate-y-2">
                <img
                  src="/images/livingroom.jpg"
                  alt="Modern furniture and home decor items"
                  className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-xl font-bold text-white mb-2">Furniture & Decor</h3>
                  <p className="text-white/90 text-sm">Document your furniture, artwork, and decorative items</p>
                </div>
              </div>
            </div>

            <div className="animate-fade-in group" style={{animationDelay: '0.1s'}}>
              <div className="relative overflow-hidden rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 group-hover:-translate-y-2">
                <img
                  src="/images/back.jpg"
                  alt="Outdoor living spaces and patio furniture"
                  className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-xl font-bold text-white mb-2">Outdoor Spaces</h3>
                  <p className="text-white/90 text-sm">Include patio furniture, outdoor equipment, and landscaping</p>
                </div>
              </div>
            </div>

            <div className="animate-fade-in group" style={{animationDelay: '0.2s'}}>
              <div className="relative overflow-hidden rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 group-hover:-translate-y-2">
                <img
                  src="/images/clock.jpg"
                  alt="Valuable items like jewelry, watches, and collectibles"
                  className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-xl font-bold text-white mb-2">Valuables & Collectibles</h3>
                  <p className="text-white/90 text-sm">Document jewelry, watches, collectibles, and high-value items</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MVP Preview with Real PDF */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="text-center mb-16 animate-slide-up">
            <h2 className="heading-2 mb-6">See It In Action</h2>
            <p className="body-large max-w-3xl mx-auto">
              Experience our documentation system with a real sample report
            </p>
          </div>
          <div className="max-w-5xl mx-auto">
            {/* PDF Preview with Real PDF */}
            <div className="animate-fade-in">
              <div className="relative">
                {/* Decorative background elements */}
                <div className="absolute -inset-4 bg-gradient-to-r from-primary-50 to-blue-50 rounded-3xl blur-xl opacity-50"></div>
                <div className="absolute -inset-2 bg-gradient-to-br from-white to-gray-50 rounded-3xl"></div>
                
                {/* Main container */}
                <div className="relative bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
                  {/* Header */}
                  <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-8 py-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">

                        <div>
                          <h3 className="text-xl font-bold text-white">ReadyToClaim Demo Report</h3>
                          <p className="text-primary-100 text-sm">Insurance-ready documentation</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={togglePdfView}
                          className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors duration-200 text-white"
                          title={isPdfMinimized ? "Maximize" : "Minimize"}
                        >
                          {isPdfMinimized ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                          ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* PDF Content */}
                  <div className="p-4 sm:p-6 md:p-8">
                    <div className="relative group">
                      {/* PDF iframe with enhanced styling */}
                      <div className={`relative overflow-hidden rounded-2xl shadow-lg border border-gray-200 bg-gray-50 transition-all duration-500 ease-in-out ${
                        isPdfMinimized ? 'h-32' : 'h-[400px] sm:h-[500px] md:h-[600px]'
                      }`}>
                        <iframe
                          src="/images/r2c-demo.pdf"
                          className={`w-full rounded-2xl transition-all duration-500 ease-in-out ${
                            isPdfMinimized ? 'h-32 opacity-50' : 'h-[400px] sm:h-[500px] md:h-[600px] opacity-100'
                          }`}
                          title="ReadyToClaim Demo PDF"
                          style={{
                            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
                          }}
                          allowFullScreen
                          loading="lazy"
                        />
                        
                        {/* Overlay gradient for depth */}
                        <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-transparent via-transparent to-gray-50/20 rounded-2xl"></div>
                        
                        {/* Interactive elements */}

                        
                        {/* Mobile-friendly PDF link */}
                        <div className="absolute bottom-4 left-4 sm:hidden">
                          <a
                            href="/images/r2c-demo.pdf"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-white/90 backdrop-blur-sm rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm border border-gray-200 hover:bg-white transition-colors"
                          >
                            📱 Open PDF
                          </a>
                        </div>

                        {/* Minimized overlay */}
                        {isPdfMinimized && (
                          <div className="absolute inset-0 bg-black/20 rounded-2xl flex items-center justify-center">
                            <div className="bg-white/90 backdrop-blur-sm rounded-lg px-4 py-2 text-sm font-semibold text-gray-700">
                              Click to expand PDF
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Decorative elements */}
                      <div className="absolute -top-2 -left-2 w-4 h-4 bg-primary-400 rounded-full opacity-60"></div>
                      <div className="absolute -bottom-2 -right-2 w-6 h-6 bg-blue-400 rounded-full opacity-40"></div>
                    </div>

                    {/* Description */}
                    <div className={`mt-8 text-center transition-all duration-500 ${
                      isPdfMinimized ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100'
                    }`}>
                      <p className="text-gray-600 max-w-2xl mx-auto">
                        This sample report demonstrates our system's ability to identify, categorize, and value household items. 
                        Each item includes detailed descriptions, market valuations, and insurance-ready documentation.
                      </p>
                      <p className="text-gray-500 text-sm mt-2 sm:hidden">
                        💡 On mobile? Tap "Open PDF" above for the best viewing experience.
                      </p>
                      <div className="mt-6 flex items-center justify-center space-x-6 text-sm text-gray-500">
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                          <span>Reverse Image Search</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                          <span>Professional Format</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                          <span>Insurance Ready</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Floating elements for visual interest */}
                <div className="absolute -top-6 -right-6 w-12 h-12 bg-gradient-to-br from-primary-200 to-primary-300 rounded-full opacity-20 animate-pulse"></div>
                <div className="absolute -bottom-8 -left-8 w-16 h-16 bg-gradient-to-br from-blue-200 to-blue-300 rounded-full opacity-20 animate-pulse" style={{animationDelay: '1s'}}></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage; 